import { useEffect, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { loadModules } from 'esri-loader';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';

import { createItem } from '../../services/items';
import { checkCoordsDigits } from '../../services/portfolios';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';

// Shared component by Loads/Batteries/Renewables for adding new item of the given type
const AddItemModal = ({ itemTypeId, updateItems, typeLabel, ...rest }) => {
    const intl = useIntl();
    const toast = useCommonToast();
    const geometry = useRef();
    const historyPush = useHistoryPush();
    const { url } = useRouteMatch();

    const books = useSelector((state) => state.item.dataForItemCreation.books);
    const pricingPoints = useSelector((state) => state.item.dataForItemCreation.pricingPoints);
    const book = useSelector((state) => state.connections.book);

    useEffect(() => {
        loadModules(['esri/geometry/support/webMercatorUtils']).then(async ([webMercatorUtils]) => {
            geometry.current = webMercatorUtils;
        });
    }, []);

    const formLabels = {
        itemName: intl.formatMessage({ id: 'common_item_name' }),
        book: intl.formatMessage({ id: 'common_book' }),
        pricingPoint: intl.formatMessage({ id: 'common_pricing_point' }),
        emptyPlaceholder: ' ',
        longitude: intl.formatMessage({ id: 'longitude' }),
        latitude: intl.formatMessage({ id: 'latitude' }),
    };

    const initialFormValues = {
        itemName: '',
        bookId: book,
        pricingPointId: '',
        latitude: '',
        longitude: '',
    };

    const validateItemName = (value) => {
        const trimmed = value.trim();

        if (!trimmed) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Item name' });
        }

        if (trimmed.length > 100) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: 'Item name', lengthRule: '100 characters or less' }
            );
        }
    };

    const isEmptyBook = (value) => {
        if (!value) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Book' });
        }
    };

    const onFormSubmit = async ({ itemName, bookId, pricingPointId, longitude, latitude }) => {
        const successMessage = intl.formatMessage({ id: 'common_item_creation_success' }, { type: typeLabel });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        const [x, y] = geometry.current.lngLatToXY(longitude, latitude);

        try {
            const { data } = await createItem({
                description: itemName,
                bookId,
                itemTypeId,
                pricingPointId: pricingPointId || null,
                longitude: longitude !== '' ? longitude : null,
                latitude: latitude !== '' ? latitude : null,
                x: longitude !== '' ? x : null,
                y: latitude !== '' ? y : null,
            });

            updateItems(data);

            historyPush(`${url}/${data.id}`);
            rest.onClose();
            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const validate = (values) => {
        let longitude = validateLongLat(values.longitude, 'long', values.latitude);
        let latitude = validateLongLat(values.latitude, 'lat', values.longitude);

        return {
            ...(longitude ? { longitude } : null),
            ...(latitude ? { latitude } : null),
        };
    };

    const validateLongLat = (val, type, sibling) => {
        let errorMessage;

        if (type === 'long') {
            if (val >= 180 || val <= -180) {
                errorMessage = intl.formatMessage({ id: 'longitude_validation_borders' });
            }
        }
        if (type === 'lat') {
            if (val > 85 || val < -85) {
                errorMessage = intl.formatMessage({ id: 'latitude_validation_borders' });
            }
        }

        if (val !== '' && sibling === '') {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_both' });
        }

        let digitsError = checkCoordsDigits(val);
        if (digitsError) {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_decimal' });
        }

        return errorMessage;
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_item_creation_title" values={{ type: typeLabel }} />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik
                    enableReinitialize
                    initialValues={initialFormValues}
                    onSubmit={onFormSubmit}
                    validate={validate}
                >
                    {({ isSubmitting, isValid, touched }) => (
                        <Form>
                            <ModalBody pb={10} borderBottom="1px" borderColor="gray.200">
                                <InputFormikField
                                    name="itemName"
                                    type="text"
                                    label={formLabels.itemName}
                                    validate={validateItemName}
                                    isRequired
                                    isFastField
                                />

                                <SimpleGrid mt={4} columns={{ base: 1, md: 2 }} spacingX={6} spacingY={4}>
                                    <ComboboxFormikField
                                        id="bookId"
                                        name="bookId"
                                        label={formLabels.book}
                                        isRequired
                                        validate={isEmptyBook}
                                        options={books}
                                        valueKey="id"
                                        labelKey="name"
                                        placeholderValue=""
                                        placeholderLabel={formLabels.emptyPlaceholder}
                                        isFastField
                                    />

                                    <ComboboxFormikField
                                        id="pricingPointId"
                                        name="pricingPointId"
                                        label={formLabels.pricingPoint}
                                        info={<FormattedMessage id="common_pricing_point_empty_tooltip" />}
                                        options={pricingPoints}
                                        labelKey="description"
                                        valueKey="id"
                                        showPlaceholder={true}
                                        placeholderValue=""
                                        isFastField
                                    />

                                    <InputFormikField
                                        name="latitude"
                                        label={formLabels.latitude}
                                        type="number"
                                        isFastField
                                    />

                                    <InputFormikField
                                        name="longitude"
                                        label={formLabels.longitude}
                                        type="number"
                                        isFastField
                                    />
                                </SimpleGrid>
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        // the below expression verifies that all required fields are populated before enabling the submit button
                                        isDisabled={!isValid || Object.keys(touched).length === 0}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="common_item_create_btn" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </ModalContent>
        </Modal>
    );
};

export default AddItemModal;
